import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { User } from './authSlice';

// The auth actions are matched by their type strings rather than by importing the auth slice.
// authSlice → api → store → branchSlice is an import cycle, and a runtime import here would
// leave `getMeAsync` undefined at the moment this module evaluates. Type-only import is erased.
const AUTH_LOGIN_FULFILLED = 'auth/login/fulfilled';
const AUTH_ME_FULFILLED = 'auth/me/fulfilled';
const AUTH_LOGOUT = 'auth/logout';

/**
 * Which branch the current person is working in (multi-branch Branch Manager, Phase 3).
 *
 * Two audiences share this state:
 *  - a Branch Manager, whose choice is validated against the branches the server says they hold
 *    (`user.branches`, from login and /auth/me) and sent on every request as `X-Branch-Id`;
 *  - an admin browsing the BM console, whose "View Branch" choice is a plain preference.
 *
 * Persistence: a manager's choice is per tab (sessionStorage, keyed by user id) so two tabs can
 * hold two branches; a new tab starts on the last branch used in this browser (localStorage), else
 * the primary. The admin choice keeps the pre-existing sessionStorage key untouched.
 *
 * The server is the only authority. Everything here is a convenience for the UI; a stale choice is
 * refused by the backend with BRANCH_ACCESS_REVOKED and lands in `accessRevoked` below.
 */

export interface BranchSummary {
  id: string;
  name: string;
  code: string;
  isPrimary: boolean;
  assignmentType: 'permanent' | 'temporary';
  endDate: string | null;
}

export interface BranchNotice {
  type: 'revoked' | 'unassigned';
  branchId: string;
  branchName: string;
}

export interface BranchState {
  ownerUserId: string | null;
  isManager: boolean;
  branches: BranchSummary[];
  primaryBranchId: string;
  activeBranchId: string;
  activeBranchName: string;
  notice: BranchNotice | null;
}

const ADMIN_KEY = 'bm_active_branch';
const sessionKey = (uid: string) => `bm_active_branch:${uid}`;
const lastKey = (uid: string) => `bm_last_branch:${uid}`;

const safe = <T,>(fn: () => T, fallback: T): T => { try { return fn(); } catch { return fallback; } };

// ── Pure rule ─────────────────────────────────────────────────────────────────────────────

/** The branches a login may work in, from the auth payload. Tolerates payloads from before Phase 1. */
export function summarizeUser(user: User | null | undefined): { isManager: boolean; branches: BranchSummary[]; primaryBranchId: string } {
  // Multi-branch technicians (30 Sep 2026): an employee login counts once the server sends it a
  // branch list — which it does only for a technician a Super Admin gave assignment rows.
  const multiBranchEmployee = user?.role === 'employee' && (user.branches?.length ?? 0) > 0;
  if (!user || (user.role !== 'branch_manager' && !multiBranchEmployee)) return { isManager: false, branches: [], primaryBranchId: '' };
  const branches: BranchSummary[] = (user.branches ?? []).map(b => ({
    id: b.branchId,
    name: b.name || b.code || b.branchId,
    code: b.code || '',
    isPrimary: !!b.isPrimary,
    assignmentType: b.assignmentType === 'temporary' ? 'temporary' : 'permanent',
    endDate: b.endDate ?? null,
  }));
  const legacy = user.primaryBranchId || user.branchId || '';
  if (legacy && !branches.some(b => b.id === legacy)) {
    branches.unshift({ id: legacy, name: legacy, code: '', isPrimary: true, assignmentType: 'permanent', endDate: null });
  }
  const primaryBranchId = branches.find(b => b.isPrimary)?.id ?? legacy ?? branches[0]?.id ?? '';
  return { isManager: true, branches, primaryBranchId };
}

/** Pick the branch to work in: the preferred one if it is still held, else the primary. */
export function resolveActiveBranch(branches: BranchSummary[], primaryBranchId: string, preferred: string | null | undefined): string {
  const p = (preferred || '').trim();
  if (p && branches.some(b => b.id === p)) return p;
  return primaryBranchId || branches[0]?.id || '';
}

const nameOf = (branches: BranchSummary[], id: string) => branches.find(b => b.id === id)?.name ?? '';

// ── Storage ───────────────────────────────────────────────────────────────────────────────

function readManagerChoice(uid: string): string | null {
  return safe(() => sessionStorage.getItem(sessionKey(uid)), null)
    ?? safe(() => localStorage.getItem(lastKey(uid)), null);
}

function readAdminChoice(): { id: string; name: string } {
  return safe(() => {
    const raw = sessionStorage.getItem(ADMIN_KEY);
    return raw ? JSON.parse(raw) : { id: '', name: '' };
  }, { id: '', name: '' });
}

function persist(state: BranchState) {
  safe(() => {
    if (!state.ownerUserId) return;
    if (state.isManager) {
      if (state.activeBranchId) {
        sessionStorage.setItem(sessionKey(state.ownerUserId), state.activeBranchId);
        localStorage.setItem(lastKey(state.ownerUserId), state.activeBranchId);
      } else {
        sessionStorage.removeItem(sessionKey(state.ownerUserId));
      }
    } else if (state.activeBranchId) {
      sessionStorage.setItem(ADMIN_KEY, JSON.stringify({ id: state.activeBranchId, name: state.activeBranchName }));
    } else {
      sessionStorage.removeItem(ADMIN_KEY);
    }
  }, undefined);
}

function clearStorage(state: BranchState) {
  safe(() => {
    sessionStorage.removeItem(ADMIN_KEY);
    if (state.ownerUserId) sessionStorage.removeItem(sessionKey(state.ownerUserId));
  }, undefined);
}

// ── State ─────────────────────────────────────────────────────────────────────────────────

const blank = (): BranchState => ({
  ownerUserId: null, isManager: false, branches: [], primaryBranchId: '',
  activeBranchId: '', activeBranchName: '', notice: null,
});

/** Re-derive the state from a fresh auth user (login, /auth/me, page load). Mutates `state`. */
function applyUser(state: BranchState, user: User | null | undefined): void {
  if (!user) { Object.assign(state, blank()); return; }
  const sameOwner = state.ownerUserId === user.id;
  const { isManager, branches, primaryBranchId } = summarizeUser(user);

  if (!isManager) {
    // Admin "View Branch" preference: kept as it was, reset only when a different person logs in.
    if (!sameOwner) {
      const stored = readAdminChoice();
      Object.assign(state, blank(), { ownerUserId: user.id, activeBranchId: stored.id || '', activeBranchName: stored.name || '' });
    } else {
      state.isManager = false; state.branches = []; state.primaryBranchId = '';
    }
    return;
  }

  const preferred = sameOwner ? state.activeBranchId : readManagerChoice(user.id);
  const next = resolveActiveBranch(branches, primaryBranchId, preferred);
  // The branch they were working in is no longer theirs (removed by a Super Admin between two
  // /auth/me calls). Say so once; the switch back to the primary happens right here.
  if (sameOwner && preferred && next !== preferred && !state.notice) {
    state.notice = { type: 'unassigned', branchId: preferred, branchName: state.activeBranchName || nameOf(state.branches, preferred) || preferred };
  }
  state.ownerUserId = user.id;
  state.isManager = true;
  state.branches = branches;
  state.primaryBranchId = primaryBranchId;
  state.activeBranchId = next;
  state.activeBranchName = nameOf(branches, next);
  persist(state);
}

const initialState: BranchState = (() => {
  const s = blank();
  applyUser(s, safe(() => JSON.parse(localStorage.getItem('hrms_user') || 'null'), null));
  return s;
})();

const branchSlice = createSlice({
  name: 'branch',
  initialState,
  reducers: {
    /** Chosen in the header switcher (manager) or the admin banner. */
    setActiveBranch: (state, action: PayloadAction<{ id: string; name?: string }>) => {
      const id = (action.payload.id || '').trim();
      if (state.isManager && id && !state.branches.some(b => b.id === id)) return; // not theirs; ignore
      state.activeBranchId = id;
      state.activeBranchName = state.isManager ? nameOf(state.branches, id) : (action.payload.name ?? '');
      persist(state);
    },
    clearActiveBranch: (state) => {
      state.activeBranchId = state.isManager ? state.primaryBranchId : '';
      state.activeBranchName = state.isManager ? nameOf(state.branches, state.activeBranchId) : '';
      persist(state);
    },
    /**
     * The server refused the selected branch (403 BRANCH_ACCESS_REVOKED). Dispatched from the API
     * client with a plain action object, so it must need nothing but this slice's own state.
     */
    accessRevoked: (state, action: PayloadAction<{ branchId?: string | null }>) => {
      const revoked = (action.payload?.branchId || state.activeBranchId || '').trim();
      const branchName = nameOf(state.branches, revoked) || revoked;
      state.branches = state.branches.filter(b => b.id !== revoked);
      if (state.primaryBranchId === revoked) state.primaryBranchId = state.branches.find(b => b.isPrimary)?.id ?? state.branches[0]?.id ?? '';
      if (state.activeBranchId === revoked || !state.branches.some(b => b.id === state.activeBranchId)) {
        state.activeBranchId = state.primaryBranchId;
        state.activeBranchName = nameOf(state.branches, state.activeBranchId);
      }
      if (!state.notice) state.notice = { type: 'revoked', branchId: revoked, branchName };
      persist(state);
    },
    clearNotice: (state) => { state.notice = null; },
  },
  extraReducers: (builder) => {
    const isType = (t: string) => (a: { type: string }): a is PayloadAction<any> => a.type === t;
    builder
      .addMatcher(isType(AUTH_LOGIN_FULFILLED), (state, action) => {
        if (action.payload?.requiresMfa) return;
        applyUser(state, (action.payload?.user ?? null) as User | null);
      })
      .addMatcher(isType(AUTH_ME_FULFILLED), (state, action) => { applyUser(state, (action.payload ?? null) as User | null); })
      .addMatcher(isType(AUTH_LOGOUT), (state) => { clearStorage(state); Object.assign(state, blank()); });
  },
});

export const { setActiveBranch, clearActiveBranch, accessRevoked, clearNotice } = branchSlice.actions;
/** Action type the API client dispatches without importing this module (avoids an import cycle). */
export const BRANCH_ACCESS_REVOKED_ACTION = 'branch/accessRevoked';
export default branchSlice.reducer;
